import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { UserPlus } from "@phosphor-icons/react";
import api, { formatApiError, LOGO_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const Field = ({ label, testId, ...props }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    <input data-testid={testId} required {...props}
      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
  </div>
);

export default function Register() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", customer_name: "", customer_location: "", email: "", password: "", id_type: "aadhaar", id_number: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) return <Navigate to="/" replace />;

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      navigate("/portal");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10">
      <div className="w-full max-w-md fade-in-up rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
        <img src={LOGO_URL} alt="DCN Food and Beverages" className="h-16 w-auto" />
        <h1 className="mt-5 text-2xl font-bold text-slate-900">Create a customer account</h1>
        <p className="mt-1 text-sm text-slate-500">Order directly from DCN Food and Beverages and track your deliveries.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <Field label="Your Name" testId="register-name-input" value={form.name} onChange={setF("name")} placeholder="Full name" />
          <Field label="Business / Shop Name" testId="register-customer-name-input" value={form.customer_name} onChange={setF("customer_name")} placeholder="e.g. Sharma Traders" />
          <Field label="Location" testId="register-location-input" value={form.customer_location} onChange={setF("customer_location")} placeholder="City / area" />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">ID Type</label>
              <select data-testid="register-id-type-select" value={form.id_type} onChange={setF("id_type")}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm outline-none">
                <option value="aadhaar">Aadhaar</option>
                <option value="gst">GST</option>
              </select>
            </div>
            <div className="col-span-2">
              <Field label={form.id_type === "gst" ? "GST Number (15 chars)" : "Aadhaar Number (12 digits)"}
                testId="register-id-number-input" value={form.id_number} onChange={setF("id_number")}
                placeholder={form.id_type === "gst" ? "e.g. 22AAAAA0000A1Z5" : "e.g. 123412341234"} />
            </div>
          </div>
          <Field label="Email" testId="register-email-input" type="email" value={form.email} onChange={setF("email")} placeholder="you@example.com" />
          <Field label="Password" testId="register-password-input" type="password" minLength={6} value={form.password} onChange={setF("password")} placeholder="Min 6 characters" />
          {error && (
            <p data-testid="register-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
          )}
          <button data-testid="register-submit-button" type="submit" disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-slate-700 disabled:opacity-60">
            <UserPlus size={16} weight="bold" />
            {loading ? "Creating account…" : "Create account"}
          </button>
        </form>
        <Link to="/login" data-testid="register-back-to-login"
          className="mt-5 block text-center text-sm text-slate-500 underline-offset-4 hover:underline">
          Already have an account? Sign in
        </Link>
      </div>
    </div>
  );
}
